import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Edit, Trash2, History, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { IconButtonWithTooltip } from "@/components/ui/icon-button-tooltip";
import { toast } from "@/hooks/use-toast";
import { OperationsHistoryModal } from "../common/OperationsHistoryModal";
import { useActivities } from "@/hooks/activity/useActivities";
import { useActivityAuditLog } from "@/hooks/activity/useActivityAuditLog";
import { Skeleton } from "@/components/ui/skeleton";
import { DeleteActivityDialog } from "./DeleteActivityDialog";
import { activityService } from "@/services/features/ActivityService";
import { useAuth } from "@/contexts/AuthContext";

export const ActivitiesTable = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedActivityForHistory, setSelectedActivityForHistory] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const {
    activities,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    entriesPerPage,
    searchValue,
    setCurrentPage,
    setEntriesPerPage,
    setSearchValue,
    handleSearch,
    clearSearch,
    refetch,
  } = useActivities();

  const { activityAuditLogs, auditLogsLoading } = useActivityAuditLog(
    selectedActivityForHistory?.activity_id || null
  );

  const handleEdit = (activity: any) => {
    navigate(`/admin/activity/edit/${activity.activity_id}`, { state: { activity } });
  };

  const handleDuplicate = (activity: any) => {
    navigate(`/admin/activity/duplicate/${activity.activity_id}`, { state: { activity } });
  };

  const handleDeleteClick = (activity: any) => {
    setActivityToDelete(activity);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!activityToDelete || !user) return;

    setIsDeleting(true);
    try {
      const authToken = localStorage.getItem("authToken") || "";
      await activityService.deleteActivity(
        Number(user.id),
        activityToDelete.activity_id,
        authToken
      );

      toast({
        title: "Success",
        description: "Activity deleted successfully",
      });

      // Refresh the activities list
      refetch();
      setDeleteDialogOpen(false);
      setActivityToDelete(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete activity",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleHistory = (activity: any) => {
    setSelectedActivityForHistory(activity);
    setIsHistoryModalOpen(true);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const startIndex = (currentPage - 1) * entriesPerPage + 1;
  const endIndex = Math.min(currentPage * entriesPerPage, totalCount);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 3;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, 3);
      } else if (currentPage >= totalPages - 1) {
        pages.push(totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(currentPage - 1, currentPage, currentPage + 1);
      }
    }
    
    return pages;
  };

  if (error) {
    return (
      <div className="text-center py-8 text-destructive">
        Error loading activities: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Input 
            className="w-64" 
            placeholder="Search by Activity Name" 
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <Button 
            className="bg-purple-600 hover:bg-purple-700 text-white px-8"
            onClick={handleSearch}
            disabled={loading}
          >
            Search
          </Button>
          {searchValue && (
            <Button 
              variant="outline"
              onClick={clearSearch}
              disabled={loading}
            >
              Clear
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Show</span>
          <Select 
            value={entriesPerPage.toString()} 
            onValueChange={(value) => setEntriesPerPage(Number(value))}
          >
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="500">500</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">Entries</span>
        </div>
      </div>
      
      <div id="table-admin-activities-container" className="rounded-md border">
        <Table id="table-admin-activities">
          <TableHeader>
            <TableRow id="table-admin-activities-header-row">
              <TableHead id="header-cell-activity-name">Activity Name</TableHead>
              <TableHead id="header-cell-patient-group">Patient Group</TableHead>
              <TableHead id="header-cell-workflow">Workflow (Initial Workflow)</TableHead>
              <TableHead id="header-cell-created-by">Created By</TableHead>
              <TableHead id="header-cell-updated-by">Updated By</TableHead>
              <TableHead id="header-cell-actions" className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody id="table-admin-activities-body">
            {loading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={idx} id={`row-activity-skeleton-${idx}`}>
                  <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                </TableRow>
              ))
            ) : activities.length === 0 ? (
              <TableRow id="row-activities-empty">
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No activities found
                </TableCell>
              </TableRow>
            ) : (
              activities.map((activity) => (
                <TableRow key={activity.activity_id} id={`row-activity-${activity.activity_id}`}>
                  <TableCell id={`cell-activity-${activity.activity_id}-name`} className="font-medium">
                    {activity.activity_name}
                  </TableCell>
                  <TableCell id={`cell-activity-${activity.activity_id}-group`}>{activity.group_name || 'N/A'}</TableCell>
                  <TableCell id={`cell-activity-${activity.activity_id}-workflow`}>{activity.workflow_name || 'N/A'}</TableCell>
                  <TableCell id={`cell-activity-${activity.activity_id}-created-by`}>{activity.created_by_name || 'N/A'}</TableCell>
                  <TableCell id={`cell-activity-${activity.activity_id}-updated-by`}>{activity.updated_by_name || 'N/A'}</TableCell>
                  <TableCell id={`cell-activity-${activity.activity_id}-actions`} className="text-right">
                    <div className="flex justify-end gap-2">
                      <IconButtonWithTooltip
                        id={`btn-activity-${activity.activity_id}-history`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleHistory(activity)}
                        tooltip="View History"
                        icon={<History className="h-4 w-4" />}
                      />
                      <IconButtonWithTooltip
                        id={`btn-activity-${activity.activity_id}-duplicate`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleDuplicate(activity)}
                        tooltip="Duplicate"
                        icon={<Copy className="h-4 w-4" />}
                      />
                      <IconButtonWithTooltip
                        id={`btn-activity-${activity.activity_id}-edit`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(activity)}
                        tooltip="Edit"
                        icon={<Edit className="h-4 w-4" />}
                      />
                      <IconButtonWithTooltip
                        id={`btn-activity-${activity.activity_id}-delete`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteClick(activity)}
                        className="text-destructive hover:text-destructive"
                        disabled={isDeleting}
                        tooltip="Delete"
                        icon={<Trash2 className="h-4 w-4" />}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      
      {totalCount > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground whitespace-nowrap">
            Showing {startIndex} to {endIndex} of {totalCount} entries
          </div>
          
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              
              {getPageNumbers().map((pageNum) => (
                <PaginationItem key={pageNum}>
                  <PaginationLink 
                    onClick={() => setCurrentPage(pageNum)}
                    isActive={currentPage === pageNum}
                    className={currentPage === pageNum 
                      ? "bg-purple-600 text-white hover:bg-purple-700 cursor-pointer" 
                      : "hover:bg-purple-100 cursor-pointer"
                    }
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              ))}
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <OperationsHistoryModal
        open={isHistoryModalOpen}
        onOpenChange={setIsHistoryModalOpen}
        entityName={selectedActivityForHistory?.activity_name || ""}
        entityType="Activity"
        auditLogs={activityAuditLogs}
        loading={auditLogsLoading}
      />

      <DeleteActivityDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        activityName={activityToDelete?.activity_name || ""}
        isDeleting={isDeleting}
      />
    </div>
  );
};